<template>
  <div
    :id="`popup-${uid}`"
    :class="['mds-popup', popupClasses]"
    :style="popupStyles"
    v-bind="$attrs"
  >
    <div
      :class="popupPointerClasses"
      class="mds-popup-pointer"
    >
      <span class="triangle"/>
    </div>

    <button v-if="closable" @click.stop="hide" class="btn-close">
      <VIcon material="close"/>
    </button>

    <!-- @slot Слот для контента -->
    <slot/>
  </div>
</template>

<style lang="scss" src="./VPopup.scss"/>

<script lang="ts">
import Constants from "@/core/misc/Constants.js";
import DomUtils      from '@/core/utils/DomUtils';
import Utils         from '@/core/utils/Utils';
import VIcon         from '@/lib/components/VIcon/VIcon.vue';

export default defineComponent({
  name: 'VPopup',

  components: {
    VIcon,
  },

  inheritAttrs: false,

  props: {
    /**
     * Попап виден
     */
    visible: {
      type:    Boolean,
      default: false,
    },
    /**
     * Элемент, около которого будет отрисован попап. Может быть query selector, ref ссылка
     * или напрямую html элемент.
     *
     * Если якоря нет (проп не задан или элемент не найден) — попап рисуется
     * от точки: левого верхнего угла открытого контекстного меню, а если меню
     * не было — от курсора. Указатель и cloud-маска в этом режиме не рисуются —
     * им нужен прямоугольник якоря.
     */
    target: {
      type: [String, Object, HTMLElement] as PropType<string | HTMLElement | { $el: HTMLElement }>,
    },
    /**
     * Отступ попапа от целевого элемента. Может быть отрицательным.
     * Без якоря — отступ от точки вправо и вниз.
     */
    offset: {
      type:    Number,
      default: 10,
    },
    /**
     * Отрисовывает указатель попапа
     */
    showPointer: {
      type:    Boolean,
      default: false,
    },
    /**
     * Запрещает отрисовку указателя попапа (обратная совместимость)
     */
    noPointer: {
      type:    Boolean,
      default: false,
    },
    /**
     * Позиция. По умолчанию попап отрисовывается снизу. Возможные значения `top, right, bottom, left`
     */
    position: {
      type:    String as PropType<'top' | 'right' | 'bottom' | 'left'>,
      default: 'bottom',
      validator(value: string) {
        return Constants.POPUP_POSITIONS.includes(value);
      },
    },
    /**
     * Вертикальное выравнивание. Работает если position равен `right` или `left`
     */
    valign: {
      type:    String as PropType<'auto' | 'top' | 'middle' | 'bottom'>,
      default: 'auto',
      validator(value: string) {
        return Constants.POPUP_VALIGNS.includes(value);
      },
    },
    /**
     * горизонтальное выравнивание. Работает если position равен `top` или `bottom`
     */
    align: {
      type:    String as PropType<'auto' | 'left' | 'middle' | 'right'>,
      default: 'auto',
      validator(value: string) {
        return Constants.POPUP_ALIGNS.includes(value);
      },
    },
    /**
     * По умолчанию попап закрывается при клике за пределами попапа или целевого элемента.
     * Это свойство отключает это поведение
     */
    notHideOnClick: {
      type:    Boolean,
      default: false,
    },
    /**
     * Включает наследование ширины попапа от целевого элемента
     */
    inheritWidth: {
      type:    Boolean,
      default: false,
    },
    /**
     * Показать кнопку закрытия. При нажатии выстрелит событие 'close'
     */
    closable: {
      type:    Boolean,
      default: false,
    },
    /**
     * добавляем внутренний отступ для контента
     */
    padding: {
      type:    String,
      default: null,
    },
    /**
     * Проп используется преимущественно из-за VPopover, так как VPopover наследуется от попапа,
     * и там есть возможность указать массив классов
     */
    cssClasses: {
      type:    Array as PropType<string[]>,
      default: null,
    },
  },

  emits: ['update:visible', 'close', 'click-outside'],

  data() {
    return {
      visibleLocal: this.visible as boolean,
      /**
       * Стабильный идентификатор инстанса. Именно в data, а не в computed:
       * data вычисляется один раз при создании компонента, id живёт всю жизнь
       * инстанса. crypto.randomUUID() в computed — недетерминированный getter:
       * при переоценке выдаёт новый id, и getPopup() перестаёт находить элемент
       * по старому id (попап молча не позиционируется и остаётся в углу экрана).
       */
      uid: crypto.randomUUID() as string,

      /**
       * Точка, от которой рисуется попап без якоря. Зафиксирована в момент
       * открытия: снимок, а не живые координаты — иначе попап уезжал бы
       * при каждом пересчёте позиции (скролл, смена target).
       */
      pointCoords: null as { x: number; y: number } | null,
    };
  },

  computed: {
    popupClasses(): Record<string, boolean> {
      const classes: Record<string, boolean> = {
        visible:      this.visibleLocal,
      };
      if (Utils.isNotEmptyArray(this.cssClasses)) {
        this.cssClasses.forEach((cssClass: string) => {
          classes[cssClass] = true;
        });
      }
      return classes;
    },
    popupPointerClasses(): { hidden: boolean } {
      return {
        hidden: !this.showPointer,
      };
    },
    popupStyles(): Record<string, string> {
      const styles: Record<string, string> = {};
      if (this.padding) {
        styles.padding = this.padding;
      }
      return styles;
    },
  },

  watch: {
    visible(value: boolean) {
      this.visibleLocal = value;
    },
    visibleLocal(value: boolean) {
      if (value) {
        this.show();
      } else {
        this.hide();
      }
    },
    target() {
      this.calculatePosition();
    },
  },

  mounted() {
    let wrapper: HTMLElement | null = this.getPopupWrapper();
    if (!wrapper) {
      wrapper    = document.createElement('div');
      wrapper.id = 'popup-wrapper';
      document.body.appendChild(wrapper);
    }
    wrapper.appendChild(this.$el);
    window.addEventListener('scroll', this.onScroll, {
      capture: true,
      passive: true,
    });
    if (this.visibleLocal) {
      this.show();
    }
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll, {
      capture: true,
      passive: true,
    } as EventListenerOptions);
    window.removeEventListener('click', this.onClickOutside);
    const wrapper: HTMLElement | null = this.getPopupWrapper();
    const popup:   HTMLElement | null = this.getPopup();
    if (popup && wrapper) {
      wrapper.removeChild(popup);
    }
    if (wrapper && wrapper.childElementCount === 0) {
      document.body.removeChild(wrapper);
    }
  },

  methods: {
    //
    // ─── ВСПОМОГАТЕЛЬНЫЕ МЕТОДЫ ──────────────────────────────────────
    //

    findInParent(selector: string): HTMLElement | null {
      if (this.$parent?.$el) {
        return this.$parent.$el.querySelector(selector);
      }
      if ((this.$parent as any)?.$?.vnode?.$el) {
        return (this.$parent as any).$.vnode.$el.querySelector(selector);
      }
      return null;
    },

    createSVG(): SVGElement {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('id', `${this.uid}_svg`);
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      svg.appendChild(path);
      return svg;
    },

    getSVG(): SVGElement {
      const popup = this.getPopup();
      let svg = popup?.children.namedItem(`${this.uid}_svg`) as SVGElement | null;
      if (!svg) {
        svg = this.createSVG();
        popup?.appendChild(svg);
      }
      return svg;
    },

    getPopupWrapper(): HTMLElement | null {
      return document.body.children.namedItem('popup-wrapper') as HTMLElement | null;
    },

    getPopup(): HTMLElement | null {
      const wrapper = this.getPopupWrapper();
      return wrapper ? wrapper.querySelector(`#popup-${this.uid}`) : null;
    },

    getPointer(): HTMLElement | null {
      const popup = this.getPopup();
      return popup ? popup.querySelector('.mds-popup-pointer') : null;
    },

    setPointerTriangleClass(placement: string): void {
      const triangle = this.getPointer()?.querySelector('.triangle');
      if (!triangle) return;
      ['top', 'right', 'bottom', 'left'].forEach((cl) => {
        if (triangle.classList.contains(cl)) {
          triangle.classList.remove(cl);
        }
      });
      triangle.classList.add(placement.split('-')[0]);
    },

    getDocumentSize(): { width: number; height: number } {
      const html = document.documentElement;
      return {
        width:  html.clientWidth,
        height: html.clientHeight,
      };
    },

    setStyles(target: HTMLElement | SVGElement | null, styles: Record<string, string>): void {
      if (target && target instanceof Element && styles instanceof Object) {
        Object.keys(styles).forEach((key) => {
          (target as HTMLElement).style[key as any] = styles[key];
        });
      }
    },

    //
    // ─── УПРАВЛЕНИЕ ВИДИМОСТЬЮ ───────────────────────────────────────
    //

    show(): void {
      const popup = this.getPopup();
      if (!popup) return;

      /**
       * Снимок точки делаем здесь, до первого позиционирования: якоря нет,
       * значит попап рисуется от точки, а точка нужна одна на всё время показа.
       */
      if (this.getAnchor() === null) {
        this.pointCoords = this.resolvePoint();
      }
      else {
        this.pointCoords = null;
      }

      popup.style.display = 'block';
      this.calculatePosition();
      popup.classList.add('visible');

      this.subscribeToClickOutside();
    },

    /**
     * Подписка на клик за пределами попапа. Отложена на следующую задачу, а не
     * на nextTick: клик, которым попап открыли, ещё всплывает, а микрозадачи
     * успевают выполниться между обработчиками — слушатель, добавленный в
     * nextTick, поймал бы тот же самый клик и закрыл попап. Без якоря
     * (режим курсора) от этого не спасает даже path.includes(anchor).
     *
     * Повторный вызов безопасен: addEventListener с той же функцией не
     * создаёт второй подписки.
     */
    subscribeToClickOutside(): void {
      if (this.notHideOnClick === true) return;

      setTimeout(() => {
        /** Попап мог закрыться или размонтироваться, пока задача ждала. */
        if (this.getPopup() === null) {
          // подписываться не на что
        }
        else {
          window.addEventListener('click', this.onClickOutside);
        }
      }, 0);
    },

    hide(): void {
      const popup = this.getPopup();
      if (!popup) return;
      popup.classList.remove('visible');

      window.removeEventListener('click', this.onClickOutside);

      if (this.visibleLocal !== false) {
        this.$emit('update:visible', false);
        this.$emit('close');
      }

      setTimeout(() => {
        // Суть в том, что состояние могло успеть обновиться за 300 мс, и уже закрывать
        // попап не надо
        if (!this.visibleLocal) {
          popup.style.cssText = '';
        }
      }, 300);
    },

    onClickOutside(event: MouseEvent): void {
      const popup  = this.getPopup();
      const anchor = this.getAnchor();
      if (!popup || this.visibleLocal === false) return;

      const path = event.composedPath();
      if (path.includes(popup)) return;
      if (anchor !== null && path.includes(anchor)) return;

      this.$emit('click-outside');
      this.hide();
    },

    //
    // ─── POSITIONING PIPELINE ────────────────────────────────────────
    //

    /**
     * Возвращает DOM-элемент якоря, относительно которого позиционируется попап.
     * Проп target может быть:
     *   - строкой (CSS-селектор — ищется в $parent)
     *   - HTMLElement напрямую
     *   - Vue-компонентом (берётся его $el)
     *
     * null означает, что якоря нет — попап рисуется от курсора.
     */
    getAnchor(): HTMLElement | null {
      if (typeof this.target === 'string') {
        return this.findInParent(this.target);
      }
      if (this.target instanceof HTMLElement) {
        return this.target;
      }
      if (this.target instanceof Object && (this.target as any)?.$el) {
        return (this.target as any).$el;
      }
      return null;
    },

    /**
     * Читает геометрию якоря в координатах viewport: позицию верхнего-левого угла
     * и размеры. Объединяет getBoundingClientRect() для координат и offsetWidth/Height
     * для размеров — как было в оригинале.
     */
    getAnchorRect(anchor: HTMLElement): { top: number; left: number; width: number; height: number } {
      const box = anchor.getBoundingClientRect();
      return {
        top:    box.top,
        left:   box.left,
        width:  anchor.offsetWidth,
        height: anchor.offsetHeight,
      };
    },

    /**
     * Читает размеры попапа. Координат у него пока нет — мы их как раз вычисляем.
     * Должно вызываться ПОСЛЕ $nextTick, если ранее применялся inheritWidth
     * (иначе прочитаем высоту от старого layout).
     */
    getPopupSize(): { width: number; height: number } {
      const popup = this.getPopup();
      if (!popup) return { width: 0, height: 0 };
      return {
        width:  popup.offsetWidth,
        height: popup.offsetHeight,
      };
    },

    /**
     * Точка, от которой рисоваться, когда якоря нет. Приоритет у открытого
     * контекстного меню: попап, вызванный его пунктом, должен встать на месте
     * меню, а не там, куда уехала мышь, пока пользователь вёл её по списку.
     * Меню не открывалось — падаем на курсор.
     */
    resolvePoint(): { x: number; y: number } {
      const contextMenuCoords: { x: number; y: number } | null
        = DomUtils.getContextMenuCoords();

      if (contextMenuCoords === null) {
        return DomUtils.getCursorCoords();
      }
      else {
        return contextMenuCoords;
      }
    },

    /**
     * Координаты попапа для режима без якоря: правее и ниже точки на offset.
     * Если с этой стороны попап не влезает в окно — сдвигаем его ровно
     * настолько, чтобы он поместился, оставив offset от края окна.
     *
     * Отражения на противоположную сторону точки здесь нет намеренно: оно
     * уводило попап на всю его ширину влево от места клика, хотя
     * не хватало нескольких десятков пикселей. Прижатие к краю оставляет
     * попап визуально в точке вызова.
     *
     * Весь остальной pipeline (сторона, выравнивание, cloud, указатель) здесь
     * не работает: он построен вокруг прямоугольника якоря, а у точки его нет.
     */
    calculatePointCoords(
      point:     { x: number; y: number },
      popupSize: { width: number; height: number },
    ): { top: number; left: number } {
      const viewport: { width: number; height: number } = this.getDocumentSize();

      let left: number = point.x + this.offset;
      let top:  number = point.y + this.offset;

      if (left + popupSize.width > viewport.width) {
        left = viewport.width - popupSize.width - this.offset;
      }
      else {
        // попап помещается правее точки — сдвигать не нужно
      }

      if (top + popupSize.height > viewport.height) {
        top = viewport.height - popupSize.height - this.offset;
      }
      else {
        // попап помещается ниже точки — сдвигать не нужно
      }

      if (left < 0) {
        left = 0;
      }

      if (top < 0) {
        top = 0;
      }

      return { top, left };
    },

    /**
     * Считает свободное место вокруг якоря: помещается ли попап с каждой из сторон
     * и хватает ли места для центрирования попапа над/рядом с якорем.
     *
     * "room" = свободное место. hasRoomAbove = есть ли место сверху и т.д.
     */
    getRoom(
      anchorRect: { top: number; left: number; width: number; height: number },
      popupSize:  { width: number; height: number },
    ): {
      hasRoomAbove:                boolean;
      hasRoomBelow:                boolean;
      hasRoomOnRight:              boolean;
      hasRoomOnLeft:               boolean;
      hasRoomToCenterOverAnchor:   boolean;
      hasRoomToCenterBesideAnchor: boolean;
    } {
      const viewport: { width: number; height: number } = this.getDocumentSize();

      return {
        // Попап помещается над якорем — хватает места между верхом якоря и верхом окна
        hasRoomAbove:
          anchorRect.top - popupSize.height - this.offset >= 0,

        // Попап помещается под якорем — хватает места между низом якоря и низом окна
        hasRoomBelow:
          anchorRect.top + anchorRect.height + this.offset + popupSize.height
          < viewport.height,

        // Попап помещается справа от якоря — хватает места между правым краем якоря и правым краем окна
        hasRoomOnRight:
          anchorRect.left + anchorRect.width + this.offset + popupSize.width
          <= viewport.width,

        // Попап помещается слева от якоря — хватает места между левым краем якоря и левым краем окна
        hasRoomOnLeft:
          anchorRect.left - popupSize.width - this.offset > 0,

        // Хватает горизонтального места, чтобы отцентрировать попап по горизонтали
        // относительно якоря (для side=top/bottom, alignment=middle)
        hasRoomToCenterOverAnchor:
          anchorRect.left + anchorRect.width / 2 - popupSize.width / 2 >= 0 &&
          anchorRect.left + anchorRect.width / 2 + popupSize.width / 2 <= viewport.width,

        // Хватает вертикального места, чтобы отцентрировать попап по вертикали
        // относительно якоря (для side=left/right, alignment=middle)
        hasRoomToCenterBesideAnchor:
          anchorRect.top + anchorRect.height / 2 - popupSize.height / 2 >= 0 &&
          anchorRect.top + anchorRect.height / 2 + popupSize.height / 2 <= viewport.height,
      };
    },

    /**
     * Выбирает сторону якоря, с которой встанет попап.
     *
     * За основу берётся желаемая сторона из пропа this.position. Если с этой
     * стороны попап не помещается — переключается на противоположную.
     */
    chooseSide(room: {
      hasRoomAbove:                boolean;
      hasRoomBelow:                boolean;
      hasRoomOnRight:              boolean;
      hasRoomOnLeft:               boolean;
      hasRoomToCenterOverAnchor:   boolean;
      hasRoomToCenterBesideAnchor: boolean;
    }): 'top' | 'right' | 'bottom' | 'left' {
      switch (this.position) {
        case 'top':
          // Если сверху есть место — ставим сверху, иначе переключаемся вниз
          if (room.hasRoomAbove) {
            return 'top';
          } else {
            return 'bottom';
          }

        case 'bottom':
          // 'bottom' — дефолтная сторона попапа. Если снизу нет места, но и сверху
          // тоже нет — остаёмся снизу, не прыгаем наверх "в никуда".
          if (room.hasRoomBelow || !room.hasRoomAbove) {
            return 'bottom';
          } else {
            return 'top';
          }

        case 'right':
          // 'right' — приоритетная сторона при горизонтальном размещении.
          // Если справа нет места, но и слева нет — остаёмся справа.
          if (room.hasRoomOnRight || !room.hasRoomOnLeft) {
            return 'right';
          } else {
            return 'left';
          }

        case 'left':
          // Если слева есть место — ставим слева, иначе переключаемся вправо
          if (room.hasRoomOnLeft) {
            return 'left';
          } else {
            return 'right';
          }

        default:
          // На случай, если в this.position прилетит что-то неожиданное —
          // возвращаем дефолтную сторону попапа
          return 'bottom';
      }
    },

    /**
     * Выбирает выравнивание попапа вдоль выбранной стороны якоря.
     *
     * Какой проп используется — зависит от стороны:
     *   - side='top' | 'bottom'  → используется this.align  (left / middle / right)
     *   - side='left' | 'right'  → используется this.valign (top / middle / bottom)
     *
     * Если проп равен 'auto' — решаем по свободному месту:
     *   - если хватает места для центрирования → 'middle'
     *   - если место есть только с одной стороны → прижимаемся к противоположной
     *   - иначе → 'middle' как безопасный дефолт
     *
     * Если проп задан явно (не 'auto') — используем его значение как есть.
     */
    chooseAlignment(
      side: 'top' | 'right' | 'bottom' | 'left',
      room: {
        hasRoomAbove:                boolean;
        hasRoomBelow:                boolean;
        hasRoomOnRight:              boolean;
        hasRoomOnLeft:               boolean;
        hasRoomToCenterOverAnchor:   boolean;
        hasRoomToCenterBesideAnchor: boolean;
      },
    ): 'left' | 'right' | 'top' | 'bottom' | 'middle' {
      // Попап сверху или снизу от якоря — выравниваем по горизонтали (this.align)
      if (side === 'top' || side === 'bottom') {
        if (this.align !== 'auto') {
          return this.align as 'left' | 'middle' | 'right';
        }
        // Режим 'auto' — выбираем на основе свободного горизонтального места
        if (room.hasRoomToCenterOverAnchor) {
          return 'middle';
        }
        if (room.hasRoomOnRight && !room.hasRoomOnLeft) {
          // Место только справа — прижимаем попап к левому краю якоря,
          // чтобы он "раскрылся" вправо
          return 'left';
        }
        if (!room.hasRoomOnRight && room.hasRoomOnLeft) {
          // Место только слева — прижимаем попап к правому краю якоря,
          // чтобы он "раскрылся" влево
          return 'right';
        }
        // Место нигде (или везде) — ставим по центру как безопасный дефолт
        return 'middle';
      }

      // Попап слева или справа от якоря — выравниваем по вертикали (this.valign)
      if (this.valign !== 'auto') {
        return this.valign as 'top' | 'middle' | 'bottom';
      }
      // Режим 'auto' — выбираем на основе свободного вертикального места
      if (room.hasRoomToCenterBesideAnchor) {
        return 'middle';
      }
      if (room.hasRoomAbove && !room.hasRoomBelow) {
        // Место только сверху — прижимаем попап к низу якоря,
        // чтобы он "раскрылся" вверх
        return 'bottom';
      }
      if (!room.hasRoomAbove && room.hasRoomBelow) {
        // Место только снизу — прижимаем попап к верху якоря,
        // чтобы он "раскрылся" вниз
        return 'top';
      }
      // Место нигде (или везде) — ставим по центру как безопасный дефолт
      return 'middle';
    },

    /**
     * Вычисляет координаты верхнего-левого угла попапа в координатах viewport
     * для выбранного placement. Чистая математика, без побочных эффектов.
     *
     * Для каждого из 12 placement'ов формула своя:
     *   - ось размещения: offset от соответствующей стороны якоря
     *   - ось выравнивания: позиция вдоль стороны якоря (начало / середина / конец)
     */
    calculatePopupCoords(
      placement:  string,
      anchorRect: { top: number; left: number; width: number; height: number },
      popupSize:  { width: number; height: number },
    ): { top: number; left: number } {
      const coords: { top: number; left: number } = { top: 0, left: 0 };

      switch (placement) {
        // ─── Попап сверху якоря ────────────────────────────────────
        case 'top-middle':
          // top попапа = top якоря - высота попапа - offset (вверх)
          // left попапа = центр якоря - половина попапа (по горизонтали)
          coords.top  = anchorRect.top - popupSize.height - this.offset;
          coords.left = anchorRect.left + anchorRect.width / 2 - popupSize.width / 2;
          break;

        case 'top-left':
          // Попап прижат к левому краю якоря
          coords.top  = anchorRect.top - popupSize.height - this.offset;
          coords.left = anchorRect.left;
          break;

        case 'top-right':
          // Попап прижат к правому краю якоря (правый край попапа = правый край якоря)
          coords.top  = anchorRect.top - popupSize.height - this.offset;
          coords.left = anchorRect.left + anchorRect.width - popupSize.width;
          break;

        // ─── Попап справа от якоря ─────────────────────────────────
        case 'right-middle':
          // left попапа = правый край якоря + offset (вправо)
          // top попапа = центр якоря - половина попапа (по вертикали)
          coords.top  = anchorRect.top + anchorRect.height / 2 - popupSize.height / 2;
          coords.left = anchorRect.left + anchorRect.width + this.offset;
          break;

        case 'right-top':
          // Попап прижат к верху якоря (верх попапа = верх якоря)
          coords.top  = anchorRect.top;
          coords.left = anchorRect.left + anchorRect.width + this.offset;
          break;

        case 'right-bottom':
          // Попап прижат к низу якоря (низ попапа = низ якоря)
          coords.top  = anchorRect.top + anchorRect.height - popupSize.height;
          coords.left = anchorRect.left + anchorRect.width + this.offset;
          break;

        // ─── Попап снизу якоря ─────────────────────────────────────
        case 'bottom-middle':
          // top попапа = низ якоря + offset (вниз)
          // left попапа = центр якоря - половина попапа (по горизонтали)
          coords.top  = anchorRect.top + anchorRect.height + this.offset;
          coords.left = anchorRect.left + anchorRect.width / 2 - popupSize.width / 2;
          break;

        case 'bottom-left':
          // Попап прижат к левому краю якоря
          coords.top  = anchorRect.top + anchorRect.height + this.offset;
          coords.left = anchorRect.left;
          break;

        case 'bottom-right':
          // Попап прижат к правому краю якоря
          coords.top  = anchorRect.top + anchorRect.height + this.offset;
          coords.left = anchorRect.left + anchorRect.width - popupSize.width;
          break;

        // ─── Попап слева от якоря ──────────────────────────────────
        case 'left-middle':
          // left попапа = левый край якоря - ширина попапа - offset (влево)
          // top попапа = центр якоря - половина попапа (по вертикали)
          coords.top  = anchorRect.top + anchorRect.height / 2 - popupSize.height / 2;
          coords.left = anchorRect.left - popupSize.width - this.offset;
          break;

        case 'left-top':
          // Попап прижат к верху якоря
          coords.top  = anchorRect.top;
          coords.left = anchorRect.left - popupSize.width - this.offset;
          break;

        case 'left-bottom':
          // Попап прижат к низу якоря
          coords.top  = anchorRect.top + anchorRect.height - popupSize.height;
          coords.left = anchorRect.left - popupSize.width - this.offset;
          break;
      }

      return coords;
    },

    /**
     * Применяет посчитанные координаты к DOM-элементу попапа.
     * Записывает top и left в popup.style. Ширина (если был inheritWidth)
     * уже была применена ранее, до $nextTick.
     */
    setPopupCoords(popupCoords: { top: number; left: number }): void {
      const popup = this.getPopup();
      if (!popup) return;

      const styles: { top?: string; left?: string } = {};
      if (popupCoords.top !== 0) {
        styles.top = `${popupCoords.top}px`;
      }
      if (popupCoords.left !== 0) {
        styles.left = `${popupCoords.left}px`;
      }

      this.setStyles(popup, styles as Record<string, string>);
    },

    /**
     * Строит форму невидимой SVG-маски ("cloud") между попапом и якорем.
     *
     * Маска нужна для непрерывности hover: курсор, двигаясь от якоря к попапу
     * через зазор offset, не выходит в пустоту — иначе hover-попапы моргают.
     *
     * Путь path охватывает прямоугольник якоря + зазор + примыкание к попапу,
     * с квадратичными кривыми (Q) в углах для плавной формы.
     *
     * Возвращает:
     *   - svgRect: где стоит <svg> относительно попапа (top/left) и какого он размера
     *   - pathD:   значение атрибута d для <path> внутри svg
     */
    getCloudShape(
      placement:   string,
      anchorRect:  { top: number; left: number; width: number; height: number },
      popupSize:   { width: number; height: number },
      popupCoords: { top: number; left: number },
    ): {
      svgRect: { top: number; left: number; width: number; height: number };
      pathD:   string;
    } {
      // Где стоит <svg> относительно попапа (top/left) и какого он размера (width/height)
      const svgRect: { top: number; left: number; width: number; height: number }
        = { top: 0, left: 0, width: 0, height: 0 };

      // Координаты точки "левый верхний угол якоря" в локальной системе координат SVG
      const anchorCoordsInSvg: { top: number; left: number }
        = { top: 0, left: 0 };

      let pathD: string = '';

      switch (placement) {
        // ─── Попап сверху якоря ────────────────────────────────────
        // SVG стоит ПОД попапом, охватывая зазор + якорь
        case 'top-middle':
        case 'top-left':
        case 'top-right':
          svgRect.top    = popupSize.height;
          svgRect.left   = popupCoords.left <= anchorRect.left
            ? 0
            : anchorRect.left - popupCoords.left;
          svgRect.width  = popupSize.width >= anchorRect.width
            ? popupSize.width
            : anchorRect.width;
          svgRect.height = anchorRect.height + this.offset;

          anchorCoordsInSvg.top  = anchorRect.top - (popupCoords.top + svgRect.top);
          anchorCoordsInSvg.left = popupCoords.left >= anchorRect.left
            ? 0
            : anchorRect.left - popupCoords.left;

          // Обводим якорь снизу-вверх, затем верхний край SVG (= низ попапа)
          pathD += `M ${anchorCoordsInSvg.left} ${anchorCoordsInSvg.top}`;
          pathD += ` v ${anchorRect.height}`;
          pathD += ` Q ${anchorCoordsInSvg.left} 0 0 0`;
          pathD += ` h ${svgRect.width}`;
          pathD += ` Q ${anchorCoordsInSvg.left + anchorRect.width} 0 ${anchorCoordsInSvg.left - 1 + anchorRect.width} ${svgRect.height}`;
          pathD += ` v -${anchorRect.height - 1}`;
          pathD += ` z`;
          break;

        // ─── Попап справа от якоря ─────────────────────────────────
        // SVG стоит СЛЕВА от попапа, охватывая зазор + якорь
        case 'right-middle':
        case 'right-top':
        case 'right-bottom':
          svgRect.top    = popupCoords.top <= anchorRect.top
            ? 0
            : anchorRect.top - popupCoords.top;
          svgRect.left   = -1 * (anchorRect.width + this.offset);
          svgRect.width  = anchorRect.width + this.offset;
          svgRect.height = popupSize.height >= anchorRect.height
            ? popupSize.height
            : anchorRect.height;

          anchorCoordsInSvg.top  = popupCoords.top >= anchorRect.top
            ? 0
            : anchorRect.top - popupCoords.top;
          anchorCoordsInSvg.left = 0;

          pathD += `M ${anchorCoordsInSvg.left} ${anchorCoordsInSvg.top}`;
          pathD += ` Q ${svgRect.width} ${anchorCoordsInSvg.top} ${svgRect.width} 0`;
          pathD += ` v ${svgRect.height}`;
          pathD += ` Q ${svgRect.width} ${anchorCoordsInSvg.top + anchorRect.height} ${anchorCoordsInSvg.left} ${anchorCoordsInSvg.top - 1 + anchorRect.height}`;
          pathD += ` h ${anchorRect.width - 1}`;
          pathD += ` v -${anchorRect.height}`;
          pathD += ` z`;
          break;

        // ─── Попап снизу якоря ─────────────────────────────────────
        // SVG стоит НАД попапом, охватывая зазор + якорь
        case 'bottom-middle':
        case 'bottom-left':
        case 'bottom-right':
          svgRect.top    = -1 * (anchorRect.height + this.offset);
          svgRect.left   = popupCoords.left <= anchorRect.left
            ? 0
            : anchorRect.left - popupCoords.left;
          svgRect.width  = popupSize.width >= anchorRect.width
            ? popupSize.width
            : anchorRect.width;
          svgRect.height = anchorRect.height + this.offset;

          anchorCoordsInSvg.top  = 0;
          anchorCoordsInSvg.left = popupCoords.left >= anchorRect.left
            ? 0
            : anchorRect.left - popupCoords.left;

          pathD += `M ${anchorCoordsInSvg.left} ${anchorCoordsInSvg.top}`;
          pathD += ` Q ${anchorCoordsInSvg.left} ${svgRect.height} 0 ${svgRect.height}`;
          pathD += ` h ${svgRect.width}`;
          pathD += ` Q ${anchorCoordsInSvg.left + anchorRect.width} ${svgRect.height} ${anchorCoordsInSvg.left - 1 + anchorRect.width} 0`;
          pathD += ` v ${anchorRect.height - 1}`;
          pathD += ` h -${anchorRect.width}`;
          pathD += ` z`;
          break;

        // ─── Попап слева от якоря ──────────────────────────────────
        // SVG стоит СПРАВА от попапа, охватывая зазор + якорь
        case 'left-middle':
        case 'left-top':
        case 'left-bottom':
          svgRect.top    = popupCoords.top <= anchorRect.top
            ? 0
            : anchorRect.top - popupCoords.top;
          svgRect.left   = popupSize.width;
          svgRect.width  = anchorRect.width + this.offset;
          svgRect.height = popupSize.height >= anchorRect.height
            ? popupSize.height
            : anchorRect.height;

          anchorCoordsInSvg.top  = popupCoords.top >= anchorRect.top
            ? 0
            : anchorRect.top - popupCoords.top;
          anchorCoordsInSvg.left = anchorRect.left - (popupCoords.left + popupSize.width);

          pathD += `M ${anchorCoordsInSvg.left} ${anchorCoordsInSvg.top}`;
          pathD += ` h ${anchorRect.width}`;
          pathD += ` Q 0 ${anchorCoordsInSvg.top} 0 0`;
          pathD += ` v ${svgRect.height}`;
          pathD += ` Q 0 ${anchorCoordsInSvg.top + anchorRect.height} ${svgRect.width - 1} ${anchorCoordsInSvg.top + anchorRect.height - 1}`;
          pathD += ` h -${anchorRect.width - 1}`;
          pathD += ` z`;
          break;
      }

      return { svgRect, pathD };
    },

    /**
     * Применяет посчитанную форму cloud к DOM.
     * Записывает размеры и viewBox в <svg>, а атрибут d — в <path> внутри него.
     */
    setCloudSvg(cloudShape: {
      svgRect: { top: number; left: number; width: number; height: number };
      pathD:   string;
    }): void {
      const svg = this.getSVG();

      // viewBox задаёт систему координат внутри SVG — в ней нарисован path
      svg.setAttributeNS(null, 'viewBox',
        `0 0 ${cloudShape.svgRect.width} ${cloudShape.svgRect.height}`);

      // Позиция и размеры SVG относительно попапа
      (svg as unknown as HTMLElement).style.top    = `${cloudShape.svgRect.top}px`;
      (svg as unknown as HTMLElement).style.left   = `${cloudShape.svgRect.left}px`;
      (svg as unknown as HTMLElement).style.width  = `${cloudShape.svgRect.width}px`;
      (svg as unknown as HTMLElement).style.height = `${cloudShape.svgRect.height}px`;

      // Путь обводки cloud
      const path: SVGPathElement | null = svg.querySelector('path');
      if (path) {
        path.setAttributeNS(null, 'd', cloudShape.pathD);
      }
    },

    /**
     * Позиционирует видимый треугольник-указатель, торчащий из попапа в сторону якоря.
     *
     * Треугольник — это <div class="triangle"> внутри .mds-popup-pointer. Его
     * размер и ориентация зависят от стороны placement (main), а смещение вдоль
     * этой стороны — от выравнивания (secondary).
     *
     * Метод вызывается только если this.showPointer && this.offset > 9 — для
     * меньшего offset треугольник физически не поместится.
     */
    setPointer(
      placement: string,
      popupSize: { width: number; height: number },
    ): void {
      const pointer: HTMLElement | null = this.getPointer();
      if (!pointer) return;

      const [side, alignment] = placement.split('-');

      // Для горизонтальных сторон (top/bottom) треугольник широкий и низкий.
      // Для вертикальных (left/right) — узкий и высокий.
      const isHorizontalSide: boolean = (side === 'top' || side === 'bottom');
      const pointerSize: { width: number; height: number } = {
        width:  isHorizontalSide ? 20 : 10,
        height: isHorizontalSide ? 10 : 20,
      };

      // Позиция треугольника относительно попапа. Вычисляется в два шага:
      // сначала "прилипание" к стороне попапа, затем смещение вдоль стороны.
      const pointerCoords: { top: number; left: number } = { top: 0, left: 0 };

      // Шаг 1: прилипаем к той стороне попапа, что смотрит на якорь
      switch (side) {
        case 'top':
          // Попап сверху якоря — треугольник торчит ВНИЗ, к якорю
          pointerCoords.top = popupSize.height - 2;
          break;
        case 'right':
          // Попап справа от якоря — треугольник торчит ВЛЕВО, к якорю
          pointerCoords.left = -1 * pointerSize.width;
          break;
        case 'bottom':
          // Попап снизу якоря — треугольник торчит ВВЕРХ, к якорю
          pointerCoords.top = -1 * pointerSize.height;
          break;
        case 'left':
          // Попап слева от якоря — треугольник торчит ВПРАВО, к якорю
          pointerCoords.left = popupSize.width - 2;
          break;
      }

      // Шаг 2: смещение вдоль стороны попапа, согласно alignment
      if (isHorizontalSide) {
        // Двигаем по горизонтали вдоль top/bottom стороны попапа
        switch (alignment) {
          case 'middle':
            pointerCoords.left = popupSize.width / 2 - pointerSize.width / 2;
            break;
          case 'left':
            pointerCoords.left = 10;
            break;
          case 'right':
            pointerCoords.left = popupSize.width - pointerSize.width - 10;
            break;
        }
      } else {
        // Двигаем по вертикали вдоль left/right стороны попапа
        switch (alignment) {
          case 'middle':
            pointerCoords.top = popupSize.height / 2 - pointerSize.height / 2;
            break;
          case 'top':
            pointerCoords.top = 10;
            break;
          case 'bottom':
            pointerCoords.top = popupSize.height - pointerSize.height - 10;
            break;
        }
      }

      // Поворот треугольника в нужную сторону — делается через CSS-класс top/right/bottom/left
      this.setPointerTriangleClass(placement);

      // Применяем размер и позицию к DOM
      this.setStyles(pointer, {
        width:  `${pointerSize.width}px`,
        height: `${pointerSize.height}px`,
        top:    `${pointerCoords.top}px`,
        left:   `${pointerCoords.left}px`,
      });
    },

    /**
     * Главный метод позиционирования. Оркеструет весь pipeline:
     * от чтения геометрии якоря до применения координат к DOM.
     *
     * Якоря нет — попап встаёт от точки, и pipeline на этом заканчивается:
     * сторона, выравнивание, cloud-маска и указатель описывают отношение попапа
     * к прямоугольнику якоря, которого в этом режиме не существует.
     */
    calculatePosition(): void {
      if (!this.visibleLocal) return;

      const anchor: HTMLElement | null = this.getAnchor();

      if (anchor === null) {
        this.calculatePositionByPoint();
      }
      else {
        this.calculatePositionByAnchor(anchor);
      }
    },

    /** Позиционирование от точки, зафиксированной в show(). */
    calculatePositionByPoint(): void {
      if (this.pointCoords === null) return;

      this.$nextTick().then(() => {
        if (this.pointCoords === null) return;

        const popupSize: { width: number; height: number }
          = this.getPopupSize();

        const popupCoords: { top: number; left: number }
          = this.calculatePointCoords(this.pointCoords, popupSize);

        this.setPopupCoords(popupCoords);
      });
    },

    /** Позиционирование от прямоугольника якоря — полный pipeline. */
    calculatePositionByAnchor(anchor: HTMLElement): void {
      // Тонкий момент: нельзя сначала прочитать размеры попапа, а потом присвоить ширину.
      // После смены ширины контент может перенестись на больше строк → высота изменится.
      // Поэтому: сначала ширина — затем $nextTick (ждём пересчёта layout) — потом чтение.
      // Багулина была настолько неочевидная, что господа бога нужно поблагодарить за то,
      // что послал решение.
      if (this.inheritWidth) {
        const popup = this.getPopup();
        if (popup) {
          this.setStyles(popup, { width: `${anchor.offsetWidth}px` });
        }
      }

      this.$nextTick().then(() => {
        // Прямоугольник якоря в координатах viewport: top/left/width/height.
        const anchorRect: { top: number; left: number; width: number; height: number }
          = this.getAnchorRect(anchor);

        // Только размеры попапа. Координат у него пока нет — их вычислим ниже.
        const popupSize: { width: number; height: number }
          = this.getPopupSize();

        // Свободное место вокруг якоря: помещается ли попап с каждой стороны
        // и хватает ли места для центрирования.
        const room: {
          hasRoomAbove:                boolean;
          hasRoomBelow:                boolean;
          hasRoomOnRight:              boolean;
          hasRoomOnLeft:               boolean;
          hasRoomToCenterOverAnchor:   boolean;
          hasRoomToCenterBesideAnchor: boolean;
        } = this.getRoom(anchorRect, popupSize);

        // Сторона якоря: за основу берём this.position, но переключаемся на
        // противоположную, если с выбранной нет места.
        const side: 'top' | 'right' | 'bottom' | 'left'
          = this.chooseSide(room);

        // Выравнивание вдоль стороны: за основу this.align/this.valign;
        // если 'auto' — решаем по свободному месту.
        const alignment: 'left' | 'right' | 'top' | 'bottom' | 'middle'
          = this.chooseAlignment(side, room);

        // Итоговое размещение — строка вида 'bottom-middle'. Дальше все switch'и
        // работают с этим одним ключом.
        const placement: string = `${side}-${alignment}`;

        // Координаты попапа в viewport для выбранного placement. Чистая математика.
        const popupCoords: { top: number; left: number }
          = this.calculatePopupCoords(placement, anchorRect, popupSize);

        // Применяем координаты к popup.style (и width, если был inheritWidth).
        this.setPopupCoords(popupCoords);

        // Форма "облачка" — невидимой SVG-маски между попапом и якорем. Нужна
        // чтобы курсор при движении через зазор offset не выходил в пустоту
        // (иначе hover-попапы моргают). Возвращает данные, без записи в DOM.
        const cloudShape: {
          svgRect: { top: number; left: number; width: number; height: number };
          pathD:   string;
        } = this.getCloudShape(placement, anchorRect, popupSize, popupCoords);

        // Записываем размеры/viewBox в <svg> и атрибут d в <path>.
        this.setCloudSvg(cloudShape);

        // Треугольник-указатель: только если включён и offset достаточен,
        // чтобы треугольник физически влез.
        if (this.showPointer && this.offset > 9) {
          this.setPointer(placement, popupSize);
        }
      });
    },

    onScroll(event: Event): void {
      const popup = this.getPopup();
      // Игнорируем скролл, если он произошёл внутри самого попапа —
      // это не движение якоря, а просто прокрутка контента (например,
      // .comments-history с overflow-y: auto), позиция попапа не изменилась.
      if (popup && event.target instanceof Node && popup.contains(event.target)) {
        return;
      }
      this.calculatePosition();
    },
  },
});
</script>
